import React, { useContext } from "react";

import { ArrowUpIcon, SpinnerIcon } from "@/components/icons";
import { ReportSlotContext } from "../canvas/reportSlot";

import {
  useGrowth,
  type GrowthMetric,
  type GrowthPillar,
  type GrowthSource,
} from "../hooks/useGrowth";
import {
  changeTone,
  formatDayMonth,
  formatSignedPct,
  formatWindows,
  GROWTH_SOURCE_LABEL as SOURCE_LABEL,
  growthTone,
  overdueNote,
  pillarSummary,
  type ChangeTone,
} from "../utils/metricChange";
import {
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
} from "./ReportPanel";
import { SourceBadge } from "./ReportViz";

interface GrowthPanelProps {
  clientId: string;
  year: number;
  month: number;
}

const TONE_TEXT: Record<ChangeTone, string> = {
  up: "text-green-600",
  down: "text-red-600",
  neutral: "text-gray-400",
};

const OverdueNote: React.FC<{ note: string | null }> = ({ note }) =>
  note ? <p className="text-xs text-gray-400 mt-2">{note}.</p> : null;

const formatCount = (n: number | null): string =>
  n == null ? "-" : n.toLocaleString();

/** Each source's same-days window, "1–26 Oct vs 1–26 Sep": one line when
 *  every source shares it, else one per source. Each line keeps its range
 *  whole; the list wraps between lines, never inside a date. */
const WindowsList: React.FC<{ metrics: ReadonlyArray<GrowthMetric> }> = ({ metrics }) => {
  const bySource = new Map<GrowthSource, string>();
  metrics.forEach((metric) => {
    if (metric.through && metric.previousThrough && !bySource.has(metric.source)) {
      bySource.set(metric.source, formatWindows(metric.through, metric.previousThrough));
    }
  });
  const windows = Array.from(bySource.entries());
  if (windows.length === 0) return null;
  const shared = new Set(windows.map(([, window]) => window)).size === 1;
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
      {shared ? (
        <li className="whitespace-nowrap">{windows[0][1]}</li>
      ) : (
        windows.map(([source, window]) => (
          <li key={source} className="whitespace-nowrap">
            <span className="text-gray-400">{SOURCE_LABEL[source]}</span> {window}
          </li>
        ))
      )}
    </ul>
  );
};

/** A provisional metric's change cell: what it waits for when it has no
 *  same-days change. */
const waitingNote = (metric: GrowthMetric): string | null =>
  metric.atMonthEnd ? "at month end" : metric.noSettledDays ? "no settled days yet" : null;

/** Every metric that feeds the pillars, previous → current, for a month
 *  whose changes wait for finalFrom. Below sm the source drops under the
 *  label so the label keeps the width. */
const MetricRows: React.FC<{ metrics: Array<GrowthMetric & { pillar: string }> }> = ({
  metrics,
}) => (
  <div className="mt-4 divide-y divide-gray-100">
    {metrics.map((metric) => (
      <div
        key={`${metric.pillar}-${metric.key}`}
        className="flex items-center gap-3 py-1.5 text-[12px]"
      >
        <span className="flex-1 min-w-0 text-gray-900">
          <span className="block truncate">
            {metric.label}
            <span className="text-gray-400"> · {metric.pillar}</span>
          </span>
          <span className="block sm:hidden text-gray-500 truncate">
            {SOURCE_LABEL[metric.source]}
          </span>
        </span>
        <span className="hidden sm:block w-28 shrink-0 text-gray-500 truncate">
          {SOURCE_LABEL[metric.source]}
        </span>
        <span className="shrink-0 sm:w-40 text-right text-gray-600 tabular-nums">
          {formatCount(metric.previous)} → {formatCount(metric.current)}
        </span>
      </div>
    ))}
  </div>
);

// The diverging pair: green right of zero, red left, a grey midline. The
// side carries the sign too, so colour is never the only cue.
const UP_COLOR = "#16A34A";
const DOWN_COLOR = "#DC2626";

/** The shared scale for every bar: the largest change rounded up to a
 *  clean step, so small months are not drawn as slivers on a ±100 axis. */
const scaleFor = (changes: ReadonlyArray<number>): number => {
  const largest = Math.max(0, ...changes.map(Math.abs));
  return [10, 25, 50, 100].find((step) => largest <= step) ?? 100;
};

/** One bar centred on zero: its length the change over the scale. */
const DivergingBar: React.FC<{ change: number; scale: number }> = ({ change, scale }) => {
  const width = `${(Math.min(Math.abs(change), scale) / scale) * 50}%`;
  return (
    <span className="relative block h-[8px]">
      <span className="absolute left-1/2 -top-1 -bottom-1 w-px bg-gray-200" />
      <span
        className={`absolute top-0 h-full ${change >= 0 ? "rounded-r-[3px]" : "rounded-l-[3px]"}`}
        style={{
          width,
          left: change >= 0 ? "50%" : undefined,
          right: change < 0 ? "50%" : undefined,
          backgroundColor: change >= 0 ? UP_COLOR : DOWN_COLOR,
        }}
      />
    </span>
  );
};

/** Each metric's change as a bar either side of zero, grouped by pillar
 *  under the pillar's own change. A metric with no change draws no bar: a
 *  provisional one names what it waits for, a missing month reads "-". */
const DivergingBars: React.FC<{
  pillars: GrowthPillar[];
  provisional: boolean;
}> = ({ pillars, provisional }) => {
  const shown = pillars
    .map((pillar) => ({
      ...pillar,
      metrics: pillar.metrics.filter((m) => m.current != null || m.previous != null),
    }))
    .filter((pillar) => pillar.metrics.length > 0);
  const scale = scaleFor(
    shown.flatMap((p) => p.metrics.map((m) => m.changePct).filter((c): c is number => c != null)),
  );
  // Under 28rem (a phone, a tablet's half) a metric's name takes its own line over its bar, so it never truncates.
  const row =
    "grid grid-cols-[minmax(0,1fr)_5.5rem] @md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_5.5rem] @3xl:grid-cols-[16rem_minmax(0,1fr)_6rem] items-center gap-x-3 gap-y-1 @md:gap-y-3";
  const wideOnly = "hidden @md:block";
  return (
    <div className="space-y-4">
      {shown.map((pillar) => (
        <div key={pillar.key}>
          <div className={`${row} mb-1`}>
            <span className="text-[12px] font-semibold text-gray-900">{pillar.label}</span>
            <span className={wideOnly} />
            <span
              className={`text-right text-[12px] font-semibold tabular-nums ${
                TONE_TEXT[changeTone(pillar.changePct)]
              }`}
            >
              {formatSignedPct(pillar.changePct)}
            </span>
          </div>
          {pillar.metrics.map((metric) => {
            const waiting = provisional ? waitingNote(metric) : null;
            const values = waiting
              ? `${formatCount(metric.current)} so far`
              : `${formatCount(metric.previous)} → ${formatCount(metric.current)}`;
            return (
              <div
                key={metric.key}
                className={`${row} py-1 text-[12px]`}
                title={`${metric.label} · ${SOURCE_LABEL[metric.source]}: ${values}`}
              >
                <span className="col-span-2 @md:col-span-1 truncate text-gray-600">{metric.label}</span>
                {metric.changePct != null && !waiting ? (
                  <DivergingBar change={metric.changePct} scale={scale} />
                ) : (
                  <span className="relative block h-[8px]">
                    <span className="absolute left-1/2 -top-1 -bottom-1 w-px bg-gray-200" />
                  </span>
                )}
                <span
                  className={`text-right tabular-nums whitespace-nowrap ${
                    waiting ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  {waiting ?? formatSignedPct(metric.changePct)}
                </span>
              </div>
            );
          })}
        </div>
      ))}
      <div className={`${row} text-[11px] text-gray-400 tabular-nums`}>
        <span className={wideOnly} />
        <span className="flex justify-between">
          <span>-{scale}%</span>
          <span>0</span>
          <span>+{scale}%</span>
        </span>
        <span />
      </div>
    </div>
  );
};

/** The folded panel's chart: one column per pillar, up for growth and
 *  down for a fall, on a shared scale around a midline, each named with
 *  its change under the name, so four fit a 300px rail; a pillar with no
 *  change has no column. */
const PillarColumns: React.FC<{ pillars: GrowthPillar[] }> = ({ pillars }) => {
  const scale = scaleFor(
    pillars.map((p) => p.changePct).filter((c): c is number => c != null),
  );
  const half = 14;
  return (
    <div className="flex gap-3" role="img" aria-label={pillarSummary(pillars)}>
      {pillars.map((pillar) => {
        const h =
          pillar.changePct == null
            ? 0
            : Math.max((Math.min(Math.abs(pillar.changePct), scale) / scale) * half, 1.5);
        const up = (pillar.changePct ?? 0) >= 0;
        return (
          <div key={pillar.key} className="flex flex-col items-center">
            <svg width={18} height={half * 2} aria-hidden>
              <line x1={0} x2={18} y1={half} y2={half} stroke="#E5E7EB" strokeWidth={1} />
              {h > 0 && (
                <rect
                  x={1}
                  y={up ? half - h : half}
                  width={16}
                  height={h}
                  fill={up ? UP_COLOR : DOWN_COLOR}
                />
              )}
            </svg>
            <span className="mt-0.5 text-[11px] leading-4 text-gray-500 whitespace-nowrap">{pillar.label}</span>
            <span className="text-[11px] leading-4 text-gray-900 tabular-nums">{formatSignedPct(pillar.changePct)}</span>
          </div>
        );
      })}
    </div>
  );
};

/** Rate of growth for /reporting: the month's composite rate against the
 *  month before, its four pillars, and every metric that fed them.
 *  Organic only; paid never feeds it (DD-021). */
export const GrowthPanel: React.FC<GrowthPanelProps> = ({
  clientId,
  year,
  month,
}) => {
  const { data: growth, isLoading, error } = useGrowth(clientId, year, month);
  const inRail = useContext(ReportSlotContext)?.kind === "rail";
  // Growth spans every source, so its badge is neutral.
  const icon = (
    <SourceBadge color="#4B5563">
      <ArrowUpIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  );

  if (isLoading) {
    const loadingRow = (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <SpinnerIcon className="w-4 h-4" />
        Loading growth…
      </div>
    );
    return (
      <ReportPanel title="Rate of growth" icon={icon} collapsedBody={loadingRow}>
        {loadingRow}
      </ReportPanel>
    );
  }

  if (error || !growth) {
    return (
      <ReportPanel
        title="Rate of growth"
        icon={icon}
        collapsedBody={<ReportPanelNote>Growth is unavailable.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="Growth is unavailable."
          hint="Could not load the rate of growth for this month."
        />
      </ReportPanel>
    );
  }

  // A metric with neither month is a source the client does not have.
  const metrics = growth.pillars.flatMap((pillar) =>
    pillar.metrics
      .filter((metric) => metric.current != null || metric.previous != null)
      .map((metric) => ({ pillar: pillar.label, ...metric })),
  );
  // A month in progress compares each metric's same-days windows, so its
  // rate is provisional until finalFrom; one with no rate falls through to
  // the not-final note.
  const provisional = !growth.complete && growth.provisional && growth.rate != null;
  // The windows list under the rate carries each source's same-days detail.
  const summary = ["vs previous month", growth.sources.map((s) => SOURCE_LABEL[s]).join(", ")]
    .filter(Boolean)
    .join(" · ");
  const overdue = overdueNote(growth.overdue);

  // A month in progress with no provisional rate: a part month against a
  // full one would always read as a fall. The values show, the changes
  // wait for finalFrom.
  if (!growth.complete && !provisional) {
    const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-US", {
      month: "long",
    });
    const notFinal = (
      <p className="text-sm text-gray-500">
        {growth.finalFrom
          ? `Growth for ${monthName} is final from ${formatDayMonth(growth.finalFrom)}.`
          : `Growth for ${monthName} is not final yet.`}
      </p>
    );
    return (
      <ReportPanel title="Rate of growth" icon={icon} summary={summary} collapsedBody={notFinal}>
        {notFinal}
        <OverdueNote note={overdue} />
        {metrics.length > 0 && (
          <div className="@container">
            <MetricRows metrics={metrics} />
          </div>
        )}
      </ReportPanel>
    );
  }

  if (growth.rate == null) {
    return (
      <ReportPanel
        title="Rate of growth"
        icon={icon}
        collapsedBody={
          <ReportPanelNote>
            {overdue ? `${overdue}.` : "Not enough data for a rate of growth."}
          </ReportPanelNote>
        }
      >
        {/* A sync that is behind is the reason when there is one. */}
        {overdue ? (
          <ReportPanelEmpty
            message={`${overdue}.`}
            hint="The rate returns once the sync catches up."
          />
        ) : (
          <ReportPanelEmpty
            message="Not enough data for a rate of growth."
            hint="Growth needs this month and the month before from at least one source: social, Business Profile or website."
          />
        )}
      </ReportPanel>
    );
  }

  const tone = growthTone(growth.rate);
  // Folded: the rate and a mini column per pillar.
  const collapsedBody = (
    <ReportHeadline
      stat={
        <ReportStat
          size="lg"
          label="Rate of growth"
          value={formatSignedPct(growth.rate)}
          tone={tone}
          note={provisional ? "to date · provisional" : undefined}
        />
      }
      // The rail item is one line of figures; the pillars wait for the open panel.
      chart={inRail ? undefined : <PillarColumns pillars={growth.pillars} />}
      inline
    />
  );

  return (
    <ReportPanel
      title="Rate of growth"
      icon={icon}
      summary={summary}
      collapsedBody={collapsedBody}
      headlineLabel="Rate of growth"
    >
      <div className="@container">
        <div className="flex flex-col @3xl:flex-row gap-4 @3xl:gap-6">
          <div className="@3xl:w-52 shrink-0 flex flex-wrap items-center gap-x-3 @3xl:block">
            <p className={`text-[40px] leading-[44px] font-semibold tracking-tight ${TONE_TEXT[tone]}`}>
              {formatSignedPct(growth.rate)}
              {provisional && <span className="text-[18px] font-medium"> to date</span>}
            </p>
            {provisional ? (
              <>
                <span className="inline-flex @3xl:mt-2 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-medium text-gray-600">
                  Provisional
                </span>
                {growth.finalFrom && (
                  <p className="text-[12px] text-gray-500 @3xl:mt-1.5">
                    Final from {formatDayMonth(growth.finalFrom)}
                  </p>
                )}
              </>
            ) : (
              <p className="text-[12px] text-gray-500 @3xl:mt-1.5">
                Mean of the pillars with data
              </p>
            )}
            <div className="basis-full">
              <OverdueNote note={overdue} />
              {provisional && <WindowsList metrics={metrics} />}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <DivergingBars pillars={growth.pillars} provisional={provisional} />
          </div>
        </div>
      </div>
    </ReportPanel>
  );
};
